import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function OrderInsuranceManagement({ orderIdProp, inModal = false, onSuccess }: { orderIdProp?: string, inModal?: boolean, onSuccess?: () => void }) {
    const params = useParams();
    const id = orderIdProp || params.id;
    const toast = useToast();
    const [order, setOrder] = useState<any>(null);
    const [insurances, setInsurances] = useState<InsuranceRecord[]>([]);
    const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingInsurance, setEditingInsurance] = useState<InsuranceRecord | null>(null);
    const [formData, setFormData] = useState({
        product_unit_id: '',
        insurance_company_id: '',
        policy_number: '',
        insurance_premium: '',
        policy_type: 'comprehensive',
        od_policy_start_date: '',
        od_policy_end_date: '',
        third_party_policy_start_date: '',
        third_party_policy_end_date: '',
        idv_amount: '',
        coverage_amount: '',
        insurance_status: 'pending'
    });

    useEffect(() => {
        loadData();
    }, [id]);

    // Handle direct add for modal view
    useEffect(() => {
        if (inModal && insurances.length === 0 && !showForm && !editingInsurance) {
            handleAdd();
        }
    }, [inModal, insurances.length, showForm, editingInsurance]);

    const loadData = async () => {
        try {
            const [orderRes, insurancesRes, companiesRes] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/insurances`),
                api.get('/insurance_companies')
            ]);
            setOrder(orderRes.data);
            const insurancesData = Array.isArray(insurancesRes.data) ? insurancesRes.data : (insurancesRes.data.data || []);
            setInsurances(insurancesData);
            setInsuranceCompanies(companiesRes.data.data || companiesRes.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load insurance data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingInsurance(null);
        setFormData({
            product_unit_id: '',
            insurance_company_id: '',
            policy_number: '',
            insurance_premium: '',
            policy_type: 'comprehensive',
            od_policy_start_date: '',
            od_policy_end_date: '',
            third_party_policy_start_date: '',
            third_party_policy_end_date: '',
            idv_amount: '',
            coverage_amount: '',
            insurance_status: 'pending'
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let response;
            if (editingInsurance) {
                response = await api.put(`/orders/${id}/insurances/${editingInsurance.id}`, { insurance: formData });
            } else {
                response = await api.post(`/orders/${id}/insurances`, formData);
            }

            if (response.success) {
                toast.success(`Insurance record ${editingInsurance ? 'updated' : 'created'} successfully`);
                loadData();
                resetForm();
                if (onSuccess) onSuccess();
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving insurance record');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving insurance:', error);
            toast.error(error.message || 'An unexpected error occurred');
        }
    };

    const handleEdit = (insurance: InsuranceRecord) => {
        setEditingInsurance(insurance);
        setFormData({
            product_unit_id: insurance.product_unit_id,
            insurance_company_id: insurance.insurance_company_id || '',
            policy_number: insurance.policy_number || '',
            insurance_premium: insurance.insurance_premium?.toString() || '',
            policy_type: insurance.policy_type || 'comprehensive',
            od_policy_start_date: insurance.od_policy_start_date || '',
            od_policy_end_date: insurance.od_policy_end_date || '',
            third_party_policy_start_date: '',
            third_party_policy_end_date: '',
            idv_amount: '',
            coverage_amount: '',
            insurance_status: insurance.insurance_status
        });
        setShowForm(true);
    };

    const handleAdd = () => {
        setEditingInsurance(null);
        setFormData({
            product_unit_id: order?.items?.length === 1 ? (order.items[0].product_unit_id || order.items[0].id) : '',
            insurance_company_id: '',
            policy_number: '',
            insurance_premium: '',
            policy_type: 'comprehensive',
            od_policy_start_date: '',
            od_policy_end_date: '',
            third_party_policy_start_date: '',
            third_party_policy_end_date: '',
            idv_amount: '',
            coverage_amount: '',
            insurance_status: 'pending'
        });
        setShowForm(true);
    };

    const handleDelete = async (insuranceId: string) => {
        if (!confirm('Are you sure you want to delete this insurance record?')) return;
        try {
            await api.delete(`/orders/${id}/insurances/${insuranceId}`);
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error deleting insurance:', error);
        }
    };


    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            pending: 'bg-gray-100 text-gray-700',
            active: 'bg-green-100 text-green-700',
            expired: 'bg-red-100 text-red-700',
            cancelled: 'bg-slate-100 text-slate-700'
        };
        return badges[status] || badges.pending;
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!order) return <div className="p-8">Order not found.</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                {!inModal && (
                    <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold mb-4">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Order
                    </Link>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`${inModal ? 'text-2xl' : 'text-3xl'} font-black text-slate-900`}>Insurance Management</h1>
                        {!inModal && <p className="text-slate-600 mt-2">Order #{order.order_number}</p>}
                    </div>
                    <button
                        onClick={handleAdd}
                        className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all text-sm"
                    >
                        + Add Insurance
                    </button>
                </div>
            </div>

            {/* Insurance Records Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Insurance Company</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Policy Number</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Type</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Status</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Premium</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {insurances.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                    No insurance records found. Click "Add Insurance" to create one.
                                </td>
                            </tr>
                        ) : (
                            insurances.map((ins) => (
                                <tr key={ins.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-medium text-slate-900">{ins.product_name || 'Unknown'}</td>
                                    <td className="px-6 py-4 text-slate-700">{ins.insurance_company_name || '-'}</td>
                                    <td className="px-6 py-4 font-mono text-slate-700">{ins.policy_number || '-'}</td>
                                    <td className="px-6 py-4 text-slate-700">{ins.policy_type || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(ins.insurance_status)}`}>
                                            {ins.insurance_status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {ins.insurance_premium ? `₹${ins.insurance_premium.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleEdit(ins)}
                                            className="text-indigo-600 hover:text-indigo-800 font-bold mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(ins.id)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className={inModal ? "mb-8 bg-slate-50 rounded-2xl p-8 border border-slate-200" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50"}>
                    <div className={inModal ? "" : "bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
                        <h2 className="text-2xl font-black text-slate-900 mb-6">
                            {editingInsurance ? 'Edit Insurance' : 'Add Insurance'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {!editingInsurance && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Product Unit</label>
                                    <select
                                        required
                                        value={formData.product_unit_id}
                                        onChange={(e) => setFormData({ ...formData, product_unit_id: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    >
                                        <option value="">Select Product</option>
                                        {order.items?.map((item: any) => (
                                            <option key={item.product_unit_id} value={item.product_unit_id}>
                                                {item.product_name} - {item.product_unit_identifier || 'No SN'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Insurance Company</label>
                                <select
                                    value={formData.insurance_company_id}
                                    onChange={(e) => setFormData({ ...formData, insurance_company_id: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value="">Select Insurance Company</option>
                                    {insuranceCompanies.map((company) => (
                                        <option key={company.id} value={company.id}>{company.company_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Policy Number</label>
                                    <input
                                        type="text"
                                        value={formData.policy_number}
                                        onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="POL123456"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Policy Type</label>
                                    <select
                                        value={formData.policy_type}
                                        onChange={(e) => setFormData({ ...formData, policy_type: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    >
                                        <option value="comprehensive">Comprehensive</option>
                                        <option value="third_party">Third Party</option>
                                        <option value="own_damage">Own Damage</option>
                                        <option value="extended_warranty">Extended Warranty</option>
                                        <option value="accidental_damage">Accidental Damage</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Insurance Status</label>
                                <select
                                    value={formData.insurance_status}
                                    onChange={(e) => setFormData({ ...formData, insurance_status: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="active">Active</option>
                                    <option value="expired">Expired</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Policy Start Date</label>
                                    <input
                                        type="date"
                                        value={formData.od_policy_start_date}
                                        onChange={(e) => setFormData({ ...formData, od_policy_start_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Policy End Date</label>
                                    <input
                                        type="date"
                                        value={formData.od_policy_end_date}
                                        onChange={(e) => setFormData({ ...formData, od_policy_end_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Insurance Premium</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.insurance_premium}
                                        onChange={(e) => setFormData({ ...formData, insurance_premium: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">IDV Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.idv_amount}
                                        onChange={(e) => setFormData({ ...formData, idv_amount: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Coverage Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.coverage_amount}
                                        onChange={(e) => setFormData({ ...formData, coverage_amount: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all"
                                >
                                    {editingInsurance ? 'Update' : 'Create'} Insurance Record
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
